import { Chip } from "@mui/material";
import moment from "moment-timezone";

const getOidcStorageKey = () => {
  const authSettings = {
    auth_server: "digi_sacco",
    client_id: "frontend",
  };
  return `oidc.user:${authSettings.auth_server}:${authSettings.client_id}`;
};

const getOidcInfo = () => {
  const key = getOidcStorageKey();
  const oidc = JSON.parse(localStorage.getItem(key) || "{}");
  return oidc;
};

export const getToken = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    return oidc.token;
  }
  return null;
};

export const getRefreshToken = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    return oidc.refreshtoken;
  }
  return null;
};

export const setToken = (token) => {
  const oidc = getOidcInfo();
  if (oidc) {
    oidc.token = token;
    localStorage.setItem(getOidcStorageKey(), JSON.stringify(oidc));
  }
};

export const setRefreshToken = (refreshtoken) => {
  const oidc = getOidcInfo();
  if (oidc) {
    oidc.refreshtoken = refreshtoken;
    localStorage.setItem(getOidcStorageKey(), JSON.stringify(oidc));
  }
};

export const logout = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    oidc.token = undefined;
    oidc.refreshtoken = undefined;
    oidc.user = undefined;
    localStorage.setItem(getOidcStorageKey(), JSON.stringify(oidc));
  }
};

export const getUserDetails = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    return oidc.user;
  }
  return null;
};

export const setUserDetails = (userDetails) => {
  const oidc = getOidcInfo();
  if (oidc) {
    oidc.user = userDetails;
    localStorage.setItem(getOidcStorageKey(), JSON.stringify(oidc));
  }
};

export const setThemeName = (themeName) => {
  const oidc = getOidcInfo();
  if (oidc) {
    oidc.themeName = themeName;
    localStorage.setItem(getOidcStorageKey(), JSON.stringify(oidc));
  }
};

export const getThemeName = () => {
  const oidc = getOidcInfo();
  return oidc?.themeName || "default";
};

export const getWindowSize = () => {
  const { innerWidth, innerHeight } = window;
  return { innerWidth, innerHeight };
};

const stringToColor = (string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

export const stringAvatar = (name) => {
  return {
    sx: {
      // bgcolor: stringToColor(name),
      width: 100,
      height: 100,
    },
    children: `${name.split(" ")[0][0]}`,
  };
};

export const checkLogin = () => {
  const isLoggedIn = getToken();

  return isLoggedIn;
};

export const ranges = (startDate, endDate) => ({
  "Today Only": [moment(startDate), moment(endDate)],
  Yesterday: [moment(startDate).subtract(1, "days"), moment(endDate)],
  "3 Days": [moment(startDate).subtract(3, "days"), moment(endDate)],
  "5 Days": [moment(startDate).subtract(5, "days"), moment(endDate)],
  "1 Week": [moment(startDate).subtract(7, "days"), moment(endDate)],
  "2 Weeks": [moment(startDate).subtract(14, "days"), moment(endDate)],
  "1 Month": [moment(startDate).subtract(1, "months"), moment(endDate)],
  "1 Year": [moment(startDate).subtract(1, "years"), moment(endDate)],
});

export let local = {
  format: "DD-MM-YYYY",
  sundayFirst: false,
};

export const getFullName = (user) => {
  const fullName = [user.first_name, user.other_names, user.surname]
    .filter((name) => name && name !== "undefined")
    .join(" ");

  return fullName;
};

export const getKycChip = (status) => {
  const normalized = status?.toLowerCase();

  const chipStyles = {
    active: { label: "active", color: "success" },
    inactive: { label: "inactive", color: "error" },
    pending: { label: "pending", color: "secondary" },
    suspended: { label: "suspended", color: "warning" },
    instalment: { label: "instalment", color: "warning" },
    incomplete: { label: "incomplete", color: "info" },
    closed: { label: "closed", color: "error" },
    blocked: { label: "Blocked", color: "error" },
    completed: { label: "completed", color: "success" },
    failed: { label: "failed", color: "error" },
    cancelled: { label: "cancelled", color: "error" },
  };

  const selected = chipStyles[normalized] || {
    label: status || "Unknown",
    color: "default",
  };

  return (
    <Chip
      label={selected.label}
      color={selected.color}
      size="small"
      sx={{
        // fontWeight: 500,
        // textTransform: "capitalize",
        // fontSize: "0.8rem",
        borderRadius: 3,
      }}
    />
  );
};

export const getStatusChip = (status) => {
  const normalized = status?.toLowerCase();

  const chipStyles = {
    completed: { label: "completed", color: "success" },
    inactive: { label: "inactive", color: "error" },
    pending: { label: "pending", color: "secondary" },
    suspended: { label: "suspended", color: "warning" },
  };

  const selected = chipStyles[normalized] || {
    label: status || "Unknown",
    color: "default",
  };

  return (
    <Chip
      label={selected.label}
      color={selected.color}
      size="small"
      sx={{
        // fontWeight: 500,
        // textTransform: "capitalize",
        // fontSize: "0.8rem",
        borderRadius: 3,
      }}
    />
  );
};

export const formatYesNo = (value) => (
  <Chip
    label={value === "YES" || value === 1 ? "Yes" : "No"}
    color={value === "YES" || value === 1 ? "success" : "error"}
    size="small"
  />
);

export const formatCurrency = (value, currency = "KES") =>
  value != null ? `${currency} ${Number(value).toLocaleString()}` : "N/A";

export const formatCurrencyV2 = (value, currency = "KES", locale = "en-KE") => {
  // 1. Check for null or undefined values first.
  if (value == null) {
    return "N/A";
  }

  // 2. Create a formatter with precise options.
  const formatter = new Intl.NumberFormat(locale, {
    style: "decimal", // We just want the number formatted, not the currency symbol from the locale.
    minimumFractionDigits: 2, // Always show at least 2 decimal places.
    maximumFractionDigits: 2, // Never show more than 2 decimal places (handles rounding).
  });

  // 3. Format the number and prepend the currency string.
  return `${currency} ${formatter.format(value)}`;
};
// export const formatDate = (date) =>
//   date
//     ? moment(date).tz("Africa/Nairobi").format("DD MMM YYYY, hh:mm A")
//     : "N/A";

export const formatDate = (date) =>
  date
    ? moment(date).subtract(3, "hours").format("YYYY-MM-DD, hh:mm A")
    : "N/A";

export const formatOptionalFee = (flag, amount, currency = "KES") => {
  if (flag === "YES" || flag === 1) {
    return amount ? formatCurrency(amount, currency) : "0";
  }
  return "N/A";
};

export const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
  { value: "suspended", label: "Suspended" },
  { value: "closed", label: "Closed" },
  { value: "pending", label: "Pending" },
  { value: "cancelled", label: "Cancelled" },
  { value: "incomplete", label: "Incomplete" },
  { value: "complete", label: "Completed" },
];
